import {
  Badge,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiArrowDown,
  FiArrowLeft,
  FiAward,
  FiBookOpen,
  FiCreditCard,
  FiGift,
  FiStar,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import type { IconType } from "react-icons";
import { useNavigate } from "react-router-dom";
import { Footer } from "../components/landing/Footer";
import { Navbar } from "../components/landing/Navbar";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { ROUTE_PATHS } from "../config/routes";

type Benefit = {
  title: string;
  description: string;
  icon: IconType;
};

type EarningRule = {
  badge: string;
  title: string;
  description: string;
  formula?: string;
  icon: IconType;
};

const BENEFITS: Benefit[] = [
  {
    title: "کارت هدیه",
    description: "تبدیل امتیازهای باشگاه به کارت هدیه و مزایای مالی منتخب",
    icon: FiCreditCard,
  },
  {
    title: "آکادمی امید",
    description: "دسترسی به دوره‌های آموزشی و محتوای تخصصی آکادمی امید",
    icon: FiBookOpen,
  },
  {
    title: "خدمات امیدیار",
    description: "استفاده از خدمات هوشمند امیدیار برای تجربه‌ای ساده‌تر",
    icon: FiZap,
  },
  {
    title: "مزایای اختصاصی",
    description: "دسترسی به سایر خدمات و جوایز ویژه اعضای باشگاه مشتریان",
    icon: FiGift,
  },
];

const EARNING_RULES: EarningRule[] = [
  {
    badge: "شروع همراهی",
    title: "اولین سرمایه‌گذاری",
    description:
      "با اولین سرمایه‌گذاری، ۱۰,۰۰۰ امتیاز به حساب باشگاه شما افزوده می‌شود.",
    icon: FiStar,
  },
  {
    badge: "از روز اول",
    title: "امتیاز روزانه سرمایه‌گذاری",
    description:
      "به ازای هر روز نگهداری سرمایه، امتیاز شما بر اساس مبلغ سرمایه‌گذاری محاسبه می‌شود.",
    formula: "X ÷ 365 × 0.004",
    icon: FiTrendingUp,
  },
  {
    badge: "بیش از ۳ ماه",
    title: "تداوم سرمایه‌گذاری",
    description:
      "در صورت حفظ سرمایه به مدت بیش از سه ماه، نرخ محاسبه امتیاز روزانه افزایش پیدا می‌کند.",
    formula: "X ÷ 365 × 0.005",
    icon: FiAward,
  },
  {
    badge: "بیش از ۶ ماه",
    title: "همراهی بلندمدت",
    description:
      "در صورت حفظ سرمایه به مدت بیش از شش ماه، امتیاز روزانه با بالاترین نرخ این دوره محاسبه می‌شود.",
    formula: "X ÷ 365 × 0.006",
    icon: FiAward,
  },
];

export const CustomerClubPage = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "dark.100");
  const elevatedBg = useColorModeValue("brand.50", "whiteAlpha.50");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const iconBg = useColorModeValue("brand.100", "icon.bg");
  const iconColor = useColorModeValue("brand.500", "icon.svg");
  const formulaBg = useColorModeValue("gray.50", "dark.900");
  const inviteBg = useColorModeValue(
    "linear(to-l, brand.50, white)",
    "linear(to-l, rgba(173,11,6,0.18), rgba(23,25,35,0.96))",
  );

  const scrollToEarningRules = () => {
    document
      .getElementById("earning-rules")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />

      <PageHero
        title="باشگاه مشتریان"
        titleHighlight="تأمین سرمایه امید"
        subtitle="باشگاه مشتریان تأمین سرمایه امید با هدف قدردانی از اعتماد و همراهی سرمایه‌گذاران طراحی شده است؛ جایی برای دریافت امتیاز و بهره‌مندی از خدمات و مزایای اختصاصی."
        primaryAction={{
          label: "نحوه کسب امتیاز",
          icon: FiArrowDown,
          onClick: scrollToEarningRules,
        }}
        secondaryAction={{
          label: "ورود به باشگاه",
          icon: FiArrowLeft,
          onClick: () => navigate(ROUTE_PATHS.DASHBOARD.CLUB),
        }}
      />

      <SectionWrapper>
        <Container maxW="container.xl">
          <VStack spacing={5} textAlign="center" mb={{ base: 8, md: 12 }}>
            <Badge
              colorScheme="brand"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="sm"
            >
              هر همراهی، یک امتیاز
            </Badge>
            <Heading
              as="h2"
              color={textColor}
              fontSize={{ base: "2xl", md: "3xl" }}
            >
              همراهی شما، ارزشمندتر از همیشه
            </Heading>
            <Text
              color={mutedColor}
              maxW="880px"
              lineHeight="2"
              fontSize={{ base: "md", md: "lg" }}
            >
              اعضای باشگاه با سرمایه‌گذاری، تداوم همراهی و دعوت از دوستان خود،
              امتیاز دریافت کرده و می‌توانند از خدمات و مزایای اختصاصی باشگاه
              بهره‌مند شوند. امتیازهای کسب‌شده این امکان را فراهم می‌کند تا از
              خدمات ویژه‌ای مانند کارت هدیه، دوره‌های آموزشی آکادمی امید، خدمات
              هوشمند امیدیار و سایر مزایای اختصاصی باشگاه مشتریان استفاده کنید.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5}>
            {BENEFITS.map((benefit) => (
              <Box
                key={benefit.title}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                p={6}
                h="100%"
                transition="all 0.25s ease"
                _hover={{
                  transform: "translateY(-4px)",
                  borderColor: "brand.200",
                  boxShadow: "lg",
                }}
              >
                <VStack align="start" spacing={4}>
                  <Box bg={iconBg} color="brand.500" p={3} borderRadius="xl">
                    <Icon
                      verticalAlign="middle"
                      as={benefit.icon as any}
                      boxSize={6}
                      color={iconColor}
                    />
                  </Box>
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    {benefit.title}
                  </Heading>
                  <Text color={mutedColor} lineHeight="1.9" fontSize="sm">
                    {benefit.description}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="earning-rules" alt>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={{ base: 8, md: 12 }}>
            <Badge
              colorScheme="brand"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="sm"
            >
              نحوه کسب امتیاز
            </Badge>
            <Heading
              as="h2"
              color={textColor}
              fontSize={{ base: "2xl", md: "3xl" }}
            >
              سرمایه‌گذاری امروز، امتیاز هر روز
            </Heading>
            <Text color={mutedColor} maxW="720px" lineHeight="1.9">
              با شروع سرمایه‌گذاری امتیاز دریافت می‌کنید و هرچه مدت همراهی شما
              بیشتر باشد، نرخ امتیاز روزانه نیز افزایش پیدا می‌کند.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {EARNING_RULES.map((rule, index) => (
              <Box
                key={rule.title}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="3xl"
                p={{ base: 6, md: 7 }}
                position="relative"
                overflow="hidden"
              >
                <HStack align="start" spacing={4}>
                  <Box
                    w="48px"
                    h="48px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg={iconBg}
                    color="brand.500"
                    borderRadius="xl"
                    flexShrink={0}
                  >
                    <Icon
                      verticalAlign="middle"
                      as={rule.icon as any}
                      boxSize={5}
                      color={iconColor}
                    />
                  </Box>
                  <VStack align="start" spacing={3} flex={1}>
                    <HStack
                      justify="space-between"
                      align="start"
                      w="100%"
                      flexWrap="wrap"
                    >
                      <Badge
                        colorScheme="brand"
                        variant="subtle"
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        {rule.badge}
                      </Badge>
                      <Text color="brand.400" fontWeight="black" fontSize="sm">
                        {String(index + 1).padStart(2, "0")}
                      </Text>
                    </HStack>
                    <Heading as="h3" fontSize="xl" color={textColor}>
                      {rule.title}
                    </Heading>
                    <Text color={mutedColor} lineHeight="1.9">
                      {rule.description}
                    </Text>
                    {rule.formula ? (
                      <Box
                        w="100%"
                        bg={formulaBg}
                        border="1px dashed"
                        borderColor="brand.200"
                        borderRadius="xl"
                        px={4}
                        py={3}
                        textAlign="center"
                        dir="ltr"
                      >
                        <Text
                          color={textColor}
                          fontWeight="extrabold"
                          fontSize={{ base: "lg", md: "xl" }}
                          letterSpacing="wide"
                        >
                          {rule.formula}
                        </Text>
                      </Box>
                    ) : null}
                  </VStack>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>

          <Text mt={6} textAlign="center" color={mutedColor} fontSize="sm">
            «X معادل مبلغ سرمایه‌گذاری شما است.»
          </Text>
        </Container>
      </SectionWrapper>

      <SectionWrapper>
        <Container maxW="container.xl">
          <Box
            bgGradient={inviteBg}
            border="1px solid"
            borderColor="brand.100"
            borderRadius="3xl"
            p={{ base: 7, md: 12 }}
            position="relative"
            overflow="hidden"
          >
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={{ base: 7, md: 10 }}
              align="center"
            >
              <Box
                w={{ base: "72px", md: "96px" }}
                h={{ base: "72px", md: "96px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="brand.500"
                color="white"
                borderRadius="3xl"
                boxShadow="xl"
                flexShrink={0}
              >
                <Icon
                  verticalAlign="middle"
                  as={FiUsers as any}
                  boxSize={{ base: 8, md: 10 }}
                />
              </Box>

              <VStack align={{ base: "center", md: "start" }} spacing={4}>
                <Heading
                  as="h2"
                  color={textColor}
                  fontSize={{ base: "2xl", md: "3xl" }}
                >
                  دعوت از دوستان
                </Heading>
                <Text
                  color={mutedColor}
                  lineHeight="2"
                  textAlign={{ base: "center", md: "right" }}
                >
                  شما می‌توانید با اشتراک‌گذاری کد معرف اختصاصی خود و دعوت از
                  دوستان و آشنایان برای عضویت و سرمایه‌گذاری، امتیازات باشگاه
                  خود را نیز افزایش دهید. هرچه افراد بیشتری از طریق کد معرف شما
                  به جمع مشتریان تأمین سرمایه امید بپیوندند، امتیاز بیشتری کسب
                  کرده و سریع‌تر به سطوح بالاتر باشگاه و مزایای ویژه آن دسترسی
                  خواهید داشت.
                </Text>
              </VStack>
            </Stack>
          </Box>
        </Container>
      </SectionWrapper>

      <SectionWrapper alt>
        <Container maxW="container.md">
          <VStack
            bg={elevatedBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="3xl"
            p={{ base: 7, md: 10 }}
            spacing={5}
            textAlign="center"
          >
            <Box bg={iconBg} color="brand.500" p={4} borderRadius="2xl">
              <Icon
                verticalAlign="middle"
                as={FiGift as any}
                boxSize={8}
                color={iconColor}
              />
            </Box>
            <Heading
              as="h2"
              color={textColor}
              fontSize={{ base: "xl", md: "2xl" }}
            >
              همراهی بیشتر، مزایای ارزشمندتر
            </Heading>
            <Text color={mutedColor} lineHeight="2" maxW="680px">
              باشگاه مشتریان تأمین سرمایه امید، راهی برای قدردانی از همراهی
              شماست؛ جایی که هرچه تعامل و همراهی شما بیشتر باشد، از خدمات،
              امکانات و جوایز ارزشمندتری بهره‌مند خواهید شد.
            </Text>
            <Button
              colorScheme="brand"
              size="lg"
              rightIcon={
                <Icon verticalAlign="middle" as={FiArrowLeft as any} />
              }
              onClick={() => navigate(ROUTE_PATHS.DASHBOARD.CLUB)}
            >
              ورود به باشگاه مشتریان
            </Button>
          </VStack>
        </Container>
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
